import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { useLoaderData, useNavigate } from 'react-router';
import { featureCreateForm, type FeatureCreateForm } from '@repo-manager/shared';
import {
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Textarea,
  toast,
} from '@repo-manager/ui';

import { getApi } from '../lib/api';
import { getActiveRepoId, useRepos } from '../lib/repo-context';
import { getSupabase } from '../lib/supabase';

interface LoaderData {
  repoId: string | null;
  milestones: { id: string; title: string }[];
}

export async function newFeatureLoader(): Promise<LoaderData> {
  const supabase = getSupabase();
  const repoId = await getActiveRepoId(supabase);
  if (!repoId) return { repoId: null, milestones: [] };
  const { data: milestones } = await supabase
    .from('milestones')
    .select('id, title')
    .eq('repo_id', repoId)
    .order('priority', { ascending: false });
  return { repoId, milestones: milestones ?? [] };
}

// Route action is unused — submission happens directly from the component
// so we can chain the Worker trigger after the Supabase insert succeeds
// and surface either failure individually. Kept exported for symmetry
// with the router config.
export async function newFeatureAction(): Promise<null> {
  return null;
}

export function NewFeaturePage() {
  const { repoId, milestones } = useLoaderData() as LoaderData;
  const { activeRepo } = useRepos();
  const scope = activeRepo && activeRepo.id === repoId ? activeRepo : null;
  const navigate = useNavigate();
  const form = useForm<FeatureCreateForm>({
    resolver: zodResolver(featureCreateForm),
    defaultValues: {
      title: '',
      narrative: '',
      success_criteria: '',
      milestone_id: null,
      priority: 0,
    },
  });

  async function onSubmit(values: FeatureCreateForm) {
    if (!repoId) {
      toast.error('No repo connected; insert one into public.repos first.');
      return;
    }
    const supabase = getSupabase();
    const { data: inserted, error } = await supabase
      .from('features')
      .insert({
        repo_id: repoId,
        title: values.title,
        narrative: values.narrative,
        success_criteria: values.success_criteria || null,
        milestone_id: values.milestone_id ?? null,
        priority: values.priority,
      })
      .select('id')
      .single();
    if (error || !inserted) {
      toast.error('Could not create feature', { description: error?.message });
      return;
    }
    try {
      await getApi().triggerFeatureCreated({ feature_id: inserted.id, repo_id: repoId });
      toast.success('Feature created; manager notified.');
    } catch (e) {
      toast.warning('Feature created, but manager trigger failed', {
        description: e instanceof Error ? e.message : String(e),
      });
    }
    navigate(`/features/${inserted.id}`);
  }

  return (
    <div className="mx-auto max-w-2xl space-y-4">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight">New feature</h1>
        {scope ? (
          <p className="text-sm text-muted-foreground">
            for <code className="font-mono">{scope.owner}/{scope.name}</code>
          </p>
        ) : !repoId ? (
          <p className="text-sm text-muted-foreground">
            No repo selected — pick one from the header dropdown.
          </p>
        ) : null}
      </div>
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Capture intent</CardTitle>
        </CardHeader>
        <CardContent>
          <Form {...form}>
            <form className="space-y-4" onSubmit={form.handleSubmit(onSubmit)}>
              <FormField
                control={form.control}
                name="title"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Title</FormLabel>
                    <FormControl>
                      <Input placeholder="Repo manager UI surfaces approval gates" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="narrative"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Narrative</FormLabel>
                    <FormControl>
                      <Textarea rows={6} {...field} />
                    </FormControl>
                    <FormDescription>What problem this solves, for whom, and what changes.</FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="success_criteria"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Success criteria (optional)</FormLabel>
                    <FormControl>
                      <Textarea rows={4} {...field} value={field.value ?? ''} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <FormField
                  control={form.control}
                  name="milestone_id"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Milestone</FormLabel>
                      <Select
                        value={field.value ?? '__none'}
                        onValueChange={(v) => field.onChange(v === '__none' ? null : v)}
                      >
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue placeholder="None" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          <SelectItem value="__none">None</SelectItem>
                          {milestones.map((m) => (
                            <SelectItem key={m.id} value={m.id}>
                              {m.title}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="priority"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Priority</FormLabel>
                      <FormControl>
                        <Input type="number" min={0} max={100} {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
              <div className="flex gap-2">
                <Button type="submit" disabled={form.formState.isSubmitting || !repoId}>
                  Create feature
                </Button>
                <Button type="button" variant="outline" onClick={() => navigate(-1)}>
                  Cancel
                </Button>
              </div>
            </form>
          </Form>
        </CardContent>
      </Card>
    </div>
  );
}
